import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import StudioPageShell from '../../components/studio/StudioPageShell';
import { getExperienceLabel } from '../../config/domainExperience';
import {
  createV2DebugProfile,
  deleteV2DebugProfile,
  listV2DebugProfiles,
} from '../../api/studioV2.api';
import { listStudioRecents, toggleStudioRecentPin } from '../../utils/studioRecents';
import StudioV2StarterGallery from '../../components/studio/StudioV2StarterGallery';
import StudioFeatureHighlights from '../../components/studio/StudioFeatureHighlights';

const STUDIO_WELCOME_KEY = 'studio_v2_welcome_dismissed_v1';

function StudioHomePage() {
  const [showWelcome, setShowWelcome] = useState(() => (
    typeof window !== 'undefined' && !window.localStorage.getItem(STUDIO_WELCOME_KEY)
  ));
  const [starterError, setStarterError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileInput, setNewProfileInput] = useState('{\n  "input": "Hello"\n}');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [recentItems, setRecentItems] = useState([]);
  const [recentSearch, setRecentSearch] = useState('');

  const refreshRecents = () => {
    setRecentItems(listStudioRecents());
  };

  useEffect(() => {
    refreshRecents();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingProfiles(true);
      const { profiles: docs, error } = await listV2DebugProfiles();
      if (cancelled) return;
      setProfiles(docs);
      setProfilesError(error);
      setLoadingProfiles(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredRecents = recentItems.filter((x) => {
    const q = recentSearch.trim().toLowerCase();
    if (!q) return true;
    return `${x.name || ''} ${x.id || ''} ${x.kind || ''}`.toLowerCase().includes(q);
  });
  const orderedRecents = [...filteredRecents].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return Number(b.t || 0) - Number(a.t || 0);
  });

  const onCreateProfile = async () => {
    setProfileMessage(null);
    setProfilesError(null);
    const name = newProfileName.trim();
    if (!name) {
      setProfilesError('Profile name is required');
      return;
    }
    let parsedInput;
    try {
      parsedInput = JSON.parse(newProfileInput);
    } catch (e) {
      setProfilesError(`Invalid input JSON: ${e.message}`);
      return;
    }
    setProfileBusy(true);
    const { profile, error } = await createV2DebugProfile(name, { input: parsedInput });
    setProfileBusy(false);
    if (error) {
      setProfilesError(error);
      return;
    }
    setProfiles((prev) => [profile, ...prev]);
    setNewProfileName('');
    setProfileMessage('Debug profile saved');
  };

  const onDeleteProfile = async (id) => {
    const profile = profiles.find((p) => String(p._id) === String(id));
    if (!window.confirm(`Delete debug profile "${profile?.name || id}"?`)) return;
    setProfileBusy(true);
    const { error } = await deleteV2DebugProfile(id);
    setProfileBusy(false);
    if (error) {
      setProfilesError(error);
      return;
    }
    setProfiles((prev) => prev.filter((p) => String(p._id) !== String(id)));
    setProfileMessage('Debug profile deleted');
  };

  const dismissWelcome = () => {
    try {
      window.localStorage.setItem(STUDIO_WELCOME_KEY, '1');
    } catch {
      /* ignore */
    }
    setShowWelcome(false);
  };

  return (
    <StudioPageShell
      title="Studio"
      subtitle={`${getExperienceLabel()} — turn real-world processes into flows, then wrap them in apps your team can use.`}
      maxWidth={880}
      actions={(
        <Button component={RouterLink} to="/dashboard" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
          Main dashboard
        </Button>
      )}
    >
      {showWelcome && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={dismissWelcome}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Welcome to Studio
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Model your scenario as a <strong>flow</strong> (steps, branches, tools), then match it to an <strong>app</strong> so chat and panels reflect how people actually work.
          </Typography>
          <Button
            component={RouterLink}
            to="/studio/help/use-cases#from-real-world-use-case-to-flow-and-app"
            variant="text"
            size="small"
            sx={{ textTransform: 'none', px: 0 }}
          >
            Guide: real-world use case → flow → app
          </Button>
        </Alert>
      )}
      {starterError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStarterError(null)}>
          {starterError}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardActionArea component={RouterLink} to="/studio/flows" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    display: 'flex',
                  })}
                >
                  <AccountTreeIcon fontSize="large" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Flows</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your real process — steps, APIs, branches, approvals — as automation.
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardActionArea component={RouterLink} to="/studio/apps" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.12),
                    color: 'secondary.main',
                    display: 'flex',
                  })}
                >
                  <ViewModuleIcon fontSize="large" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Apps</Typography>
                  <Typography variant="body2" color="text.secondary">
                    How people see it — chat and panels wired to flow outputs.
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardActionArea component={RouterLink} to="/studio/forms" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: 'info.main',
                    display: 'flex',
                  })}
                >
                  <DescriptionOutlinedIcon fontSize="large" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Forms</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Structured inputs your flows and apps can reuse.
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <StudioFeatureHighlights
          title="Explore the platform"
          subtitle="Extensions, MCP tools, in-app help with architecture diagrams, and recruiting playbooks."
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2.5,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          From a real job to Studio
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>1.</strong>
          {' '}
          Break the scenario into steps, hand-offs, and system calls.
          {' '}
          <strong>2.</strong>
          {' '}
          Encode that as a flow (graph or templates below).
          {' '}
          <strong>3.</strong>
          {' '}
          Choose an app shell and bind panels to what the flow returns.
        </Typography>
        <Button
          component={RouterLink}
          to="/studio/help/use-cases#from-real-world-use-case-to-flow-and-app"
          size="small"
          variant="text"
          sx={{ textTransform: 'none', px: 0 }}
        >
          Expand in Help
        </Button>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 3,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="h6">Starter templates</Typography>
              <Button
                component={RouterLink}
                to="/studio/templates"
                variant="text"
                size="small"
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, textTransform: 'none' }}
              >
                Full-page catalog
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Pick a shape close to your scenario (permits, intake, support, dashboards), create a draft, then adapt nodes and bindings to your facts.
            </Typography>
          </Box>
          <StudioV2StarterGallery mode="both" variant="full" onError={setStarterError} />
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2.5,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={(theme) => ({ color: theme.palette.primary.main, mt: 0.25, display: 'flex' })}>
              <TerminalOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Pro builders</Typography>
              <Typography variant="body2" color="text.secondary">
                VS Code: edit <code>*.flow.v2.json</code> on disk, validate, run sync or SSE stream to your API, then bring changes back into Studio — start under Help → VS Code.
              </Typography>
            </Box>
          </Stack>
          <Button
            component={RouterLink}
            to="/studio/help/builders#vs-code-extension-and-deployment-apis"
            variant="outlined"
            size="small"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, textTransform: 'none', flexShrink: 0 }}
          >
            VS Code setup
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 3,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="h6">Extensions &amp; tools</Typography>
          <Typography variant="body2" color="text.secondary">
            Enable plugins (Clio, InterviewFlow skills), browse the unified MCP tool catalog, and manage tenant capabilities.
          </Typography>
          <Button
            component={RouterLink}
            to="/studio/extensions"
            variant="outlined"
            startIcon={<TuneIcon />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            Manage extensions
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 0,
          mb: 2,
          p: 3,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="h6">Help and support</Typography>
          <Typography variant="body2" color="text.secondary">
            Telemetry, Prometheus, Run / Debug profiles, and troubleshooting — plus links to repository guides.
          </Typography>
          <Button
            component={RouterLink}
            to="/studio/help/overview"
            variant="contained"
            color="secondary"
            startIcon={<HelpOutlineIcon />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            Help and features
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">Debug profiles</Typography>
          <Typography variant="body2" color="text.secondary">
            Save reusable execution inputs for V2 flow runs.
          </Typography>
          {profilesError && <Alert severity="error">{profilesError}</Alert>}
          {profileMessage && <Alert severity="success">{profileMessage}</Alert>}
          <TextField
            label="Profile name"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            disabled={profileBusy}
            size="small"
          />
          <TextField
            label="Input JSON"
            value={newProfileInput}
            onChange={(e) => setNewProfileInput(e.target.value)}
            multiline
            minRows={4}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            disabled={profileBusy}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onCreateProfile} disabled={profileBusy}>
              {profileBusy ? 'Saving…' : 'Save profile'}
            </Button>
          </Stack>
          {loadingProfiles ? (
            <Typography variant="body2" color="text.secondary">Loading profiles…</Typography>
          ) : profiles.length === 0 ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3, px: 1, textAlign: 'center' }}>
              <Box
                sx={(theme) => ({
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: theme.palette.action.hover,
                  color: 'text.secondary',
                })}
              >
                <TuneIcon />
              </Box>
              <Typography variant="subtitle2" fontWeight={600}>No debug profiles</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                Save named inputs above, then open a flow and pick a profile when you run it.
              </Typography>
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Stack divider={<Divider flexItem />}>
                {profiles.map((p) => (
                  <Stack
                    key={p._id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1.5}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: 'background.paper',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={(theme) => ({
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                        })}
                      >
                        <TuneIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {p?.payload?.flowId
                            ? `Bound to flow · ${p.payload.flowId}`
                            : 'Global input profile'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {p?.payload?.flowId && (
                        <Button
                          size="small"
                          variant="outlined"
                          component={RouterLink}
                          to={`/studio/flows/${p.payload.flowId}?debugProfileId=${encodeURIComponent(String(p._id))}`}
                          sx={{ textTransform: 'none' }}
                        >
                          Open in flow
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => onDeleteProfile(p._id)}
                        disabled={profileBusy}
                        sx={{ textTransform: 'none' }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 3,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">Recents & pins</Typography>
          <TextField
            size="small"
            label="Search recents"
            value={recentSearch}
            onChange={(e) => setRecentSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {orderedRecents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No recent Studio items yet. Open a flow or app editor to populate this list.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Stack divider={<Divider flexItem />}>
                {orderedRecents.slice(0, 12).map((item) => (
                  <Stack key={`${item.kind}_${item.id}`} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
                    <Button
                      component={RouterLink}
                      to={item.href || (item.kind === 'flow' ? `/studio/flows/${item.id}` : `/studio/apps/${item.id}`)}
                      sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                    >
                      {`${item.kind.toUpperCase()} · ${item.name || item.id}`}
                    </Button>
                    <Button
                      size="small"
                      variant={item.pinned ? 'contained' : 'outlined'}
                      startIcon={<PushPinOutlinedIcon />}
                      onClick={() => setRecentItems(toggleStudioRecentPin(item.kind, item.id))}
                      sx={{ textTransform: 'none' }}
                    >
                      {item.pinned ? 'Pinned' : 'Pin'}
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
    </StudioPageShell>
  );
}

export default StudioHomePage;
